import { Tag } from 'antd';
import classNames from 'classnames';
import React from 'react';

import { InventoryTabItemName } from '../../../../../InventoryStore';
import { useInventoryState, useReadOnlyRowConfig } from '../../../../../hooks';
import { isDateAfterEndOfSale, isDatePast } from '../../../../../utils';
import { PackType } from '../../../../../utils/packs';
import type { PackDetails } from '../../../../../utils/packs';
import {
  VARIANT_CONFIG,
  getVariantFromScore,
} from '../../../../Common/Score/scoreUtils';
import { LabelCell } from '../../../../Table/Cells/LabelCell/LabelCell';
import { LabelValueCell } from '../../../../Table/Cells/LabelValueCell/LabelValueCell';
import { PureFieldCell } from '../../../../Table/Cells/PureFieldCell/PureFieldCell';
import { TableRow } from '../../../../Table/TableRow/TableRow';
import styles from '../../PacksTab.module.scss';

const PackPricingScoreRowComponent: React.FC<{ packId: number }> = ({
  packId,
}) => {
  const { endOfSaleDate } = useInventoryState();
  const { inventoryPacks, dateKeys } = useReadOnlyRowConfig(
    InventoryTabItemName.Packs
  );

  if (!inventoryPacks) {
    return null;
  }

  return (
    <TableRow>
      <LabelCell label="Pricing score" />
      {dateKeys.map((dateKey) => {
        const fieldName = [dateKey, `${packId}`];
        const isPast = isDatePast(dateKey);
        const isGrayed = isPast || isDateAfterEndOfSale(dateKey, endOfSaleDate);

        return (
          <PureFieldCell key={dateKey} name={fieldName} dateKey={dateKey}>
            {(values?: PackDetails) => {
              if (!values || values.type !== PackType.WITH_ROOM) {
                return null;
              }

              const pricingScore =
                values.packScore !== undefined &&
                (values.priceFrom !== undefined ||
                  values.priceFromDiscount !== undefined)
                  ? Math.round(values.packScore)
                  : undefined;
              const variant = getVariantFromScore(pricingScore);
              const config = VARIANT_CONFIG[variant];

              const bg = isPast
                ? VARIANT_CONFIG['default'].backgroundColor
                : config.backgroundColor;

              const borderColor = isPast
                ? VARIANT_CONFIG['default'].borderColor
                : config.borderColor;

              const color = isPast
                ? VARIANT_CONFIG['default'].strokeColor
                : config.strokeColor;

              const tag = (
                <Tag
                  className={classNames(styles.tagPriceScore, {
                    [styles.pastDate]: isPast,
                  })}
                  style={{
                    backgroundColor: bg,
                    borderColor: borderColor,
                    color: color,
                  }}
                >
                  {pricingScore !== undefined ? pricingScore : '-'}
                </Tag>
              );

              return (
                <LabelValueCell
                  name={fieldName}
                  date={dateKey}
                  formatter={() => tag}
                  value={pricingScore}
                  grayed={isGrayed}
                />
              );
            }}
          </PureFieldCell>
        );
      })}
    </TableRow>
  );
};

export const PackPricingScoreRow = React.memo(PackPricingScoreRowComponent);
